import { access, readdir, stat } from 'fs/promises';
import { join, relative, dirname } from 'path';
import chalk from 'chalk';
import ora from 'ora';
import { success, error, warning, info } from '../utils/prompt.js';
import { loadConfig } from '../utils/config-loader.js';
import { convertFromMemory, normalizeTargetFormat } from '../converters/index.js';
import { CompileOptions, TargetFormat } from '../types/index.js';
import { IgnoreMatcher } from '../utils/ignore-matcher.js';

/**
 * Compile command - convert MEMORY.md to target format(s)
 */
export async function compileCommand(options: CompileOptions): Promise<void> {
  const cwd = process.cwd();
  const config = await loadConfig(cwd);

  console.log(chalk.bold('\n⚙️  Compile MEMORY.md\n'));

  // Initialize ignore matcher
  const ignoreMatcher = new IgnoreMatcher(cwd, config);
  await ignoreMatcher.initialize();

  // Find all MEMORY.md files recursively
  const memoryFiles = await findMemoryFiles(cwd, ignoreMatcher);

  if (memoryFiles.length === 0) {
    error('No MEMORY.md files found');
    info('Run "mem2 import --from <file>" to create MEMORY.md first');
    process.exit(1);
  }

  // Determine targets
  let targets: string[];
  if (options.to && options.to.length > 0) {
    targets = options.to;
  } else {
    targets = config.compile.defaultTargets;
  }

  // Remove duplicates and normalize
  const uniqueTargets = deduplicateTargets(targets);

  console.log(chalk.dim(`Found ${memoryFiles.length} MEMORY.md file(s)`));
  console.log(chalk.dim(`Targets: ${uniqueTargets.join(', ')}\n`));

  // Compile each MEMORY.md file
  let hasWarnings = false;
  let hasErrors = false;
  let totalCompiled = 0;

  for (const memoryPath of memoryFiles) {
    const dirPath = dirname(memoryPath);
    const relativePath = relative(cwd, dirPath);
    const displayPath = relativePath ? relativePath + '/' : '';

    for (const target of uniqueTargets) {
      const spinner = ora(`Compiling ${displayPath}MEMORY.md to ${chalk.cyan(target)}...`).start();

      try {
        const outputFile = join(dirPath, target);
        const result = await convertFromMemory(
          memoryPath,
          outputFile,
          target as TargetFormat,
          config.compile
        );

        spinner.succeed(`Compiled ${displayPath}MEMORY.md to ${chalk.cyan(target)}`);
        totalCompiled++;

        // Show warnings
        if (result.warnings.length > 0) {
          hasWarnings = true;
          result.warnings.forEach((warn) => {
            warning(`  ${warn}`);
          });
        }

        // Show errors (in non-strict mode)
        if (result.errors.length > 0) {
          hasErrors = true;
          result.errors.forEach((err) => {
            error(`  ${err}`);
          });
        }
      } catch (err) {
        spinner.fail(`Failed to compile ${displayPath}MEMORY.md to ${chalk.cyan(target)}`);
        error(`  ${(err as Error).message}`);
        hasErrors = true;
      }
    }
  }

  console.log();

  if (hasErrors && config.compile.strictMode) {
    error('Compilation failed with errors');
    info('Set compile.strictMode to false in .mem2rc.json to ignore import errors');
    process.exit(1);
  } else if (hasWarnings || hasErrors) {
    warning(`Compiled ${totalCompiled} file(s) with warnings`);
  } else {
    success(`Successfully compiled ${totalCompiled} file(s)!`);
  }

  console.log();
}

/**
 * Recursively find all MEMORY.md files
 */
async function findMemoryFiles(dir: string, ignoreMatcher: IgnoreMatcher, basePath?: string): Promise<string[]> {
  const results: string[] = [];
  const base = basePath || dir;

  try {
    const entries = await readdir(dir, { withFileTypes: true });

    for (const entry of entries) {
      const fullPath = join(dir, entry.name);
      const relativePath = relative(base, fullPath);

      // Check if should ignore (using relative path)
      if (ignoreMatcher.shouldIgnore(relativePath)) {
        continue;
      }

      if (entry.isDirectory()) {
        // Recursively search subdirectories
        const subResults = await findMemoryFiles(fullPath, ignoreMatcher, base);
        results.push(...subResults);
      } else if (entry.isFile() && entry.name === 'MEMORY.md') {
        results.push(fullPath);
      }
    }
  } catch (err) {
    // Silently skip directories we can't read
  }

  return results;
}

/**
 * Deduplicate targets (e.g., "codex" and "gemini" both map to AGENTS.md)
 */
function deduplicateTargets(targets: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const target of targets) {
    try {
      const normalized = normalizeTargetFormat(target as TargetFormat);
      if (!seen.has(normalized)) {
        seen.add(normalized);
        result.push(normalized);
      }
    } catch (err) {
      // Invalid format, skip
      warning(`Unknown target format: ${target}`);
    }
  }

  return result;
}

/**
 * Check if file exists
 */
async function fileExists(path: string): Promise<boolean> {
  try {
    await access(path);
    return true;
  } catch {
    return false;
  }
}
